import { useEffect, useState } from 'react';
import { tonnageOptions, starOptions } from '../data/catalogue';
import { KIT_COPPER_METRES } from '../data/pricing';

// Compact filter bar for the listing grid.
//
// Mobile shows a single row: a Filters button plus a horizontally scrollable
// chip strip. Star rating, piping kit, inverter and sort move into a bottom
// sheet. Three wrapped chip rows plus the kit explainer used to consume the
// entire first screen on a phone, so no product card appeared above the fold
// and the page read as an article rather than a shop.
//
// Desktop keeps every chip visible.
//
// Used by the main price list AND by all 12 collection pages. Collection pages
// pass the options that actually exist in their own range (tonChoices,
// starChoices, showKit, showInverter), so a 1 Ton page never offers a 2 Ton
// chip that returns nothing. With no options passed, the bar behaves exactly
// as it does on /daikin-ac-price-list-delhi.
//
// The kit explainer is a <details>: collapsed, not deleted, so it stays indexed.

// Popular sizes first, so 1.5 and 2.0 Ton are reachable without scrolling.
const TON_ORDER = ['1.5', '1.0', '2.0', '0.8', '1.8', '2.5'];

function orderedTons(list) {
  const ranked = TON_ORDER.filter((t) => list.includes(t));
  return [...ranked, ...list.filter((t) => !ranked.includes(t))];
}

export default function ListingFilterBar({
  ton, setTon,
  star, setStar,
  kit, setKit,
  inverterOnly, setInverterOnly,
  sort, setSort,
  sorts,
  count,
  tonChoices,
  starChoices,
  showKit = true,
  showInverter = true,
}) {
  const [sheet, setSheet] = useState(false);

  // A bottom sheet that lets the page scroll underneath it feels broken.
  useEffect(() => {
    if (!sheet) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, [sheet]);

  const chip = (active) =>
    active
      ? 'shrink-0 rounded-full bg-blue-600 px-3.5 py-1.5 text-xs font-medium text-white'
      : 'shrink-0 rounded-full border border-gray-300 px-3.5 py-1.5 text-xs text-gray-600 transition hover:border-blue-400 hover:text-blue-600';

  const clearAll = () => {
    setTon('all');
    setStar('all');
    setKit('all');
    setInverterOnly(false);
  };

  const noFilters = ton === 'all' && star === 'all' && kit === 'all' && !inverterOnly;
  const activeCount = [ton !== 'all', star !== 'all', kit !== 'all', inverterOnly]
    .filter(Boolean).length;

  const desktopTons = (tonChoices || tonnageOptions()).map(String);
  const tons = orderedTons(desktopTons);
  const stars = starChoices || starOptions();
  const showTons = tons.length > 1;
  const showStars = stars.length > 1;

  const tonChip = (t) => (
    <button
      key={`t${t}`}
      type="button"
      onClick={() => setTon(ton === t ? 'all' : t)}
      className={chip(ton === t)}
    >
      {t} Ton
    </button>
  );

  const starChip = (s) => (
    <button
      key={`s${s}`}
      type="button"
      onClick={() => setStar(star === String(s) ? 'all' : String(s))}
      className={chip(star === String(s))}
    >
      {s} Star
    </button>
  );

  // Mobile quick row: tonnage where there is a choice of size, otherwise star
  // rating (a 1 Ton page has one size but may have two ratings).
  const quickChips = showTons ? tons.map(tonChip) : showStars ? stars.map(starChip) : [];

  return (
    <>
      {/* Sticky on mobile so filters stay reachable down a long grid. */}
      <div className="sticky top-16 z-30 -mx-4 mt-4 border-b border-gray-200 bg-white/95 px-4 pb-2 pt-2.5 backdrop-blur lg:static lg:top-auto lg:mx-0 lg:mt-6 lg:border-0 lg:bg-transparent lg:px-0 lg:pb-0 lg:pt-0 lg:backdrop-blur-none">

        {/* ── mobile: one row ───────────────────────────────────────────── */}
        <div className="flex items-center gap-2 lg:hidden">
          <button
            type="button"
            onClick={() => setSheet(true)}
            aria-label="Open filters"
            className={`flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition ${
              activeCount
                ? 'bg-blue-600 text-white'
                : 'border border-gray-300 text-gray-700'
            }`}
          >
            <svg viewBox="0 0 20 20" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.8">
              <path d="M3 6h14M6 10h8M8.5 14h3" strokeLinecap="round" />
            </svg>
            Filters
            {activeCount > 0 && <span>· {activeCount}</span>}
          </button>

          {quickChips.length > 0 && (
            <div
              className="flex flex-1 gap-2 overflow-x-auto [&::-webkit-scrollbar]:hidden"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              <button type="button" onClick={clearAll} className={chip(noFilters)}>All</button>
              {quickChips}
            </div>
          )}
        </div>

        {/* ── desktop: everything visible ──────────────────────────────── */}
        <div className="hidden lg:block lg:space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={clearAll} className={chip(noFilters)}>All</button>
            {showTons && desktopTons.map(tonChip)}
            {showStars && stars.map(starChip)}
            {showInverter && (
              <button
                type="button"
                onClick={() => setInverterOnly(!inverterOnly)}
                className={chip(inverterOnly)}
              >
                Inverter
              </button>
            )}
          </div>

          {showKit && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-gray-500">Piping kit</span>
              <button type="button" onClick={() => setKit('all')} className={chip(kit === 'all')}>Show both</button>
              <button type="button" onClick={() => setKit('withKit')} className={chip(kit === 'withKit')}>With kit</button>
              <button type="button" onClick={() => setKit('withoutKit')} className={chip(kit === 'withoutKit')}>Without kit</button>
            </div>
          )}
        </div>
      </div>

      {/* ── count and sort on one line ──────────────────────────────────── */}
      <div className="mt-2.5 flex items-center justify-between gap-3 lg:mt-4">
        <p className="text-xs text-gray-500">{count} option{count === 1 ? '' : 's'}</p>
        <label className="flex items-center gap-1.5">
          <span className="sr-only">Sort by</span>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs text-gray-700"
          >
            {Object.entries(sorts).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
        </label>
      </div>

      {/* ── kit explainer, collapsed but present in the HTML ─────────────── */}
      {showKit && (
        <details className="group mt-2">
          <summary className="cursor-pointer list-none text-xs text-gray-500 marker:hidden">
            <span className="inline-flex items-center gap-1">
              <svg viewBox="0 0 20 20" className="h-3.5 w-3.5 transition-transform group-open:rotate-90" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M8 5l5 5-5 5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Why some models appear twice
            </span>
          </summary>
          <p className="mt-1.5 text-xs leading-relaxed text-gray-500">
            Models are listed twice where both versions exist. With kit includes{' '}
            {KIT_COPPER_METRES} m of copper piping; without kit is ₹1,500 less and suits a
            replacement where the existing piping can be reused.
          </p>
        </details>
      )}

      {/* ── mobile filter sheet ─────────────────────────────────────────── */}
      {sheet && (
        <div className="fixed inset-0 z-[70] lg:hidden">
          <button
            type="button"
            aria-label="Close filters"
            onClick={() => setSheet(false)}
            className="absolute inset-0 h-full w-full bg-black/40"
          />

          <div
            role="dialog"
            aria-modal="true"
            aria-label="Filters"
            className="absolute inset-x-0 bottom-0 max-h-[80vh] overflow-y-auto rounded-t-2xl bg-white px-4 pb-6 pt-4"
          >
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-900">Filters</h2>
              <button
                type="button"
                onClick={() => setSheet(false)}
                aria-label="Close filters"
                className="rounded-md p-1.5 text-gray-400 transition hover:bg-gray-100 hover:text-gray-700"
              >
                <svg viewBox="0 0 20 20" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <path d="M5 5l10 10M15 5L5 15" strokeLinecap="round" />
                </svg>
              </button>
            </div>

            {showTons && (
              <>
                <p className="text-xs font-medium text-gray-900">Capacity</p>
                <div className="mb-5 mt-2 flex flex-wrap gap-2">
                  <button type="button" onClick={() => setTon('all')} className={chip(ton === 'all')}>Any</button>
                  {tons.map(tonChip)}
                </div>
              </>
            )}

            {showStars && (
              <>
                <p className="text-xs font-medium text-gray-900">Star rating</p>
                <div className="mb-5 mt-2 flex flex-wrap gap-2">
                  <button type="button" onClick={() => setStar('all')} className={chip(star === 'all')}>Any</button>
                  {stars.map(starChip)}
                </div>
              </>
            )}

            {showKit && (
              <>
                <p className="text-xs font-medium text-gray-900">Piping kit</p>
                <div className="mb-5 mt-2 flex flex-wrap gap-2">
                  <button type="button" onClick={() => setKit('all')} className={chip(kit === 'all')}>Show both</button>
                  <button type="button" onClick={() => setKit('withKit')} className={chip(kit === 'withKit')}>With kit</button>
                  <button type="button" onClick={() => setKit('withoutKit')} className={chip(kit === 'withoutKit')}>Without kit</button>
                </div>
              </>
            )}

            {showInverter && (
              <>
                <p className="text-xs font-medium text-gray-900">Compressor</p>
                <div className="mb-5 mt-2 flex flex-wrap gap-2">
                  <button type="button" onClick={() => setInverterOnly(false)} className={chip(!inverterOnly)}>Any</button>
                  <button type="button" onClick={() => setInverterOnly(true)} className={chip(inverterOnly)}>Inverter only</button>
                </div>
              </>
            )}

            <p className="text-xs font-medium text-gray-900">Sort by</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {Object.entries(sorts).map(([k, v]) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setSort(k)}
                  className={chip(sort === k)}
                >
                  {v.label}
                </button>
              ))}
            </div>

            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={clearAll}
                className="flex-1 rounded-md border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
              >
                Clear all
              </button>
              <button
                type="button"
                onClick={() => setSheet(false)}
                className="flex-1 rounded-md bg-blue-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-blue-700"
              >
                Show {count} option{count === 1 ? '' : 's'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}